import React from "react";
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import IosShareRoundedIcon from "@mui/icons-material/IosShareRounded";

const CaseSearchDialogHeader = ({
  component,
  onClose,
  isCopied,
  onCopy,
  onShare,
  onPrint,
}) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  const actions = [
    ...(component !== "preview"
      ? [
          {
            label: "Copy",
            icon: <ContentCopyOutlinedIcon sx={iconStyle} />,
            onClick: onCopy,
            bg: "linear-gradient(135deg, #4ECDC4 0%, #45B7AF 100%)",
            shadow: "rgba(78, 205, 196, 0.4)",
            tooltip: isCopied
              ? "Copied"
              : `Copy ${component === "preview" ? "Judgement" : "Summary"}`,
          },
          {
            label: "Share",
            icon: <IosShareRoundedIcon sx={iconStyle} />,
            onClick: onShare,
            bg: "linear-gradient(135deg, #45B7E3 0%, #2E86DE 100%)",
            shadow: "rgba(46, 134, 222, 0.4)",
          },
        ]
      : []),
    {
      label: "Print/Save as PDF",
      icon: <FileDownloadOutlinedIcon sx={iconStyle} />,
      onClick: onPrint,
      bg: "linear-gradient(135deg, #FF9F43 0%, #F19036 100%)",
      shadow: "rgba(255, 159, 67, 0.4)",
    },
  ];

  return (
    <Box
      sx={{
        minHeight: 48,
        display: "flex",
        alignItems: "center",
        px: { xs: 1.5, sm: 2 },
        background:
          component === "preview"
            ? "linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)"
            : "linear-gradient(135deg, #A78BFA 0%, #7C3AED 100%)",
        position: "sticky",
        top: 0,
        zIndex: 10,
        borderTopLeftRadius: { xs: 0, sm: "8px" },
        borderTopRightRadius: { xs: 0, sm: "8px" },
        flexWrap: "wrap", // allows wrapping on smaller screens
        gap: { xs: 1, sm: 0 },
      }}
    >
      <IconButton onClick={onClose} sx={iconButtonStyle} aria-label="Back">
        <ChevronLeftRoundedIcon sx={{ fontSize: 18 }} />
      </IconButton>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flex: 1,
          minWidth: 0,
        }}
      >
        <ArticleOutlinedIcon
          sx={{ color: "#ffffff", mr: 0.75, fontSize: 20 }}
        />
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            color: "#ffffff",
            fontSize: "0.875rem",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {component === "preview" ? "Original Judgement" : "Case Summary"}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: { xs: 1, sm: 1.5 },
          flexWrap: "wrap",
          justifyContent: { xs: "center", sm: "flex-end" },
          mt: { xs: 1, sm: 0 },
        }}
      >
        {actions.map((action, index) => (
          <Tooltip
            key={index}
            title={action.tooltip || action.label}
            arrow
            placement="bottom"
          >
            <Box
              onClick={action.onClick}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                color: "#ffffff",
                cursor: "pointer",
                transition: "all 0.2s ease",
                "&:hover .icon-container": {
                  transform: "scale(1.05)",
                  boxShadow: `0 4px 12px ${action.shadow}`,
                },
              }}
            >
              <Box
                className="icon-container"
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: action.bg,
                  boxShadow: `0 2px 8px ${action.shadow}`,
                  transition: "all 0.2s ease",
                }}
              >
                {action.icon}
              </Box>
              {!isXs && (
                <span
                  style={{
                    fontSize: "0.8125rem",
                    fontWeight: 500,
                    color: "#ffffff",
                    transition: "all 0.2s ease",
                    fontFamily: '"Roboto", sans-serif',
                  }}
                >
                  {action.label}
                </span>
              )}
            </Box>
          </Tooltip>
        ))}
      </Box>
    </Box>
  );
};

const iconStyle = {
  fontSize: 14,
  color: "#ffffff",
  filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.1))",
};

const iconButtonStyle = {
  color: "#ffffff",
  background: "rgba(255, 255, 255, 0.15)",
  mr: 1.5,
  "&:hover": {
    background: "rgba(255, 255, 255, 0.25)",
  },
  p: 0.5,
  borderRadius: "6px",
};

export default CaseSearchDialogHeader;
